<script lang="ts">
  import type { FacetValue } from "../facet-value";
  import type { Facet, FacetOptions } from "./type.ts";
  import { List } from "../facet-value-group";

  import type { ClassValue } from "svelte/elements";
  import {
    addNameAndFeatureTracking,
    isNba,
    isNotPopular,
    isNotSelected,
    isPopular,
    isSelected,
  } from "./facetValues.ts";
  import { featureTrackingAttributes, removeStatusIf } from "../tracking";
  import { initToggles, isActive } from "../../toggle/Toggle.ts";
  import { setFilterTitleMode } from "../filter";
  import { heureka } from "@otto-ec/heureka";
  import { type SizePreferences, SizePreferencesCookieStore } from "./CookieStore.ts";
  import { preselected } from "./preselection.svelte.ts";
  import { sortByOrder, sortFacetValuesByOrder } from "./Sorter.ts";
  import { tracking } from "@otto-ec/tracking-bct";

  /*                                                                           */
  /*        */
  /*                                                                           */

  const SIZE_TYPE_CONFIG = {
    shoesizeeuv2: { key: "shoeSizeEUV2" as const, label: "EU" },
    shoesizeusv2: { key: "shoeSizeUSV2" as const, label: "US" },
    shoesizeukv2: { key: "shoeSizeUKV2" as const, label: "UK" },
  } as const;

  type SizeTypeId = keyof typeof SIZE_TYPE_CONFIG;

  const DEFAULT_SIZE_PREFERENCES: SizePreferences = {
    shoeSizeEUV2: [],
    shoeSizeUSV2: [],
    shoeSizeUKV2: [],
  };

  /*                                                                           */
  /*    */
  /*                                                                           */

  interface Props extends Facet, FacetOptions {
    class?: ClassValue;
    gemerkt: string[];
  }

  let {
    id,
    name,
    title,
    featureTracking,
    visibility,
    values = [],
    showTitle = false,
    filterTitleMode = "onlyTitle",
    mode = "default",
    parentId = "",
    hasNbaValues = false,
    facetLocation,
    showPopularValues,
    contentSnippet = defaultList,
    ...rest
  }: Props = $props();

  /*                                                                           */
  /*                    */
  /*                                                                           */

  function getSizeConfig(sizeId: string) {
    return SIZE_TYPE_CONFIG[sizeId as SizeTypeId];
  }

  function extractSize(prefs: SizePreferences, sizeId: string): string[] {
    const config = getSizeConfig(sizeId);
    return config ? (prefs[config.key] ?? []) : [];
  }

  function updateSizePreferences(prefs: SizePreferences, sizeId: string, titles: string[]): SizePreferences {
    const config = getSizeConfig(sizeId);
    if (!config) return prefs;

    return {
      ...prefs,
      [config.key]: [...new Set(titles)],
    };
  }

  function formatSizePreferences(prefs: SizePreferences): string {
    return Object.values(SIZE_TYPE_CONFIG)
      .map(({ key, label }) => {
        const sizes = prefs[key];
        return sizes?.length ? `${label} ${sizes.join(`; ${label} `)}` : "";
      })
      .filter(Boolean)
      .join("; ");
  }

  function hasAnySavedSizes(prefs: SizePreferences): boolean {
    return Object.values(SIZE_TYPE_CONFIG).some(({ key }) => (prefs[key]?.length ?? 0) > 0);
  }

  function getSizeLabel(facetId: string): string {
    const sizeId = facetId.replace("facet_", "");
    return getSizeConfig(sizeId)?.label ?? "";
  }

  function isSizeSaved(sizeValue: string, prefs: SizePreferences, sizeId: string): boolean {
    const config = getSizeConfig(sizeId);
    return config ? (prefs[config.key]?.includes(sizeValue) ?? false) : false;
  }

  function resetAllSizes(prefs: SizePreferences): void {
    Object.values(SIZE_TYPE_CONFIG).forEach(({ key }) => {
      prefs[key] = [];
    });
  }

  function restoreSizes(prefs: SizePreferences, restored: SizePreferences): void {
    Object.values(SIZE_TYPE_CONFIG).forEach(({ key }) => {
      prefs[key] = restored[key] ?? [];
    });
  }

  /*                                                                           */
  /*    */
  /*                                                                           */

  initToggles();

  const facetId = `facet_${id}`;
  const hidden = false; /*                                               */

  let showDeletionBanner = $state(false);
  const valuesDisplayName = $state(values.map((v) => v.title));

  const sizes: { map: SizePreferences } = $state({
    map: SizePreferencesCookieStore.get() ?? { ...DEFAULT_SIZE_PREFERENCES },
  });

  let gemerkt = $state(sortByOrder(extractSize(sizes.map, id), valuesDisplayName));
  let dynamicSelectedValues = $state(
    values.filter(isSelected).map(addNameAndFeatureTracking(facetId, name, 1, mode, facetLocation)),
  );

  /*                                                                           */
  /*             */
  /*                                                                           */

  const namedValues = $derived(values.map(addNameAndFeatureTracking(facetId, name, 1, mode, facetLocation)));

  const selectedValues = $derived(
    values.filter(isSelected).map(addNameAndFeatureTracking(facetId, name, 1, mode, facetLocation)),
  );

  const popularValues = $derived(
    values
      .filter(isPopular)
      .map(addNameAndFeatureTracking(facetId, name, selectedValues.length + 1, mode, facetLocation)),
  );

  const unselectedNbas = $derived(
    values
      .filter(isNotPopular)
      .filter(isNotSelected)
      .filter(isNba)
      .map(
        addNameAndFeatureTracking(facetId, name, selectedValues.length + popularValues.length + 1, mode, facetLocation),
      ),
  );

  const savedNamedValues = $derived.by(() =>
    namedValues.map((v) => ({ ...v, saved: isSizeSaved(v.title, sizes.map, id) })),
  );

  /*                       */
  const selectedTitles = $derived(new Set(dynamicSelectedValues.map((v) => v.title)));
  const gemerktSet = $derived(new Set(gemerkt));

  const entfernen = $derived(dynamicSelectedValues.length > 0 && selectedTitles.isSubsetOf(gemerktSet));
  const merken = $derived(!entfernen && dynamicSelectedValues.length > 0);
  const alleEntfernen = $derived(!entfernen && !merken && gemerkt.length > 0);
  const showActionButton = $derived(merken || entfernen || alleEntfernen);

  const shouldBePlural = $derived(
    (!entfernen && !merken && gemerkt.length > 1) ||
      (entfernen && dynamicSelectedValues.length > 1) ||
      (merken && dynamicSelectedValues.length > 1),
  );

  /*                                                                           */
  /*      */
  /*                                                                           */

  $effect(() => {
    if (!isActive("HEUREKA_1975_OC_COMPONENTS_MIGRATION")) {
      const effectiveMode =
        filterTitleMode === "onlyPopularValuesAndTitle" && popularValues.length === 0 ? "onlyTitle" : filterTitleMode;
      setFilterTitleMode(parentId, effectiveMode);
    } else if (showPopularValues) {
      /*                                                                    */
      if (filterTitleMode === "onlyPopularValuesAndTitle" && popularValues.length === 0) {
        setFilterTitleMode(parentId, "onlyTitle");
      }
    }
  });

  $effect(() => {
    sortFacetValuesByOrder(dynamicSelectedValues, values);
  });

  /*                                                                           */
  /*             */
  /*                                                                           */

  function handleSaveCookie() {
    const newSizes = [...gemerkt, ...dynamicSelectedValues.map((v) => v.title)];
    const sortedNewSizes = sortByOrder(newSizes, valuesDisplayName);
    const newSizePreferences = updateSizePreferences(sizes.map, id, sortedNewSizes);

    SizePreferencesCookieStore.add(getSizeConfig(id).label, sortedNewSizes);
    sizes.map = newSizePreferences;
    gemerkt = newSizes;

    /*                                                                            */
    const valuesToTrack = values
      .filter((v) => {
        const preselectedState = $preselected[v.id];
        /*                                                                      */
        if (preselectedState !== undefined) {
          return preselectedState;
        }
        /*                                               */
        return v.checked;
      })
      .map((v) => v.id);

    /*                                      */
    heureka.filterPersonalization_track.emit({
      action: "save",
      facetId: id,
      values: valuesToTrack,
    });
  }

  function handleDeleteCookieValues() {
    const deletedValueIds = dynamicSelectedValues.map((v) => v.id);
    const deletedTitles = new Set(dynamicSelectedValues.map((v) => v.title));
    const remainingSizes = gemerkt.filter((g) => !deletedTitles.has(g));

    /*                                             */
    const remainingSavedValueIds = remainingSizes
      .map((title) => {
        const value = values.find((v) => v.title === title);
        return value?.id ?? "";
      })
      .filter(Boolean);

    const newSizes = updateSizePreferences(sizes.map, id, remainingSizes);

    SizePreferencesCookieStore.delete(getSizeConfig(id).label, [...deletedTitles]);
    sizes.map = newSizes;
    gemerkt = remainingSizes;

    /*                                      */
    heureka.filterPersonalization_track.emit({
      action: "delete",
      facetId: id,
      values: deletedValueIds,
      remainingSavedValues: remainingSavedValueIds,
    });
  }

  function handleDeleteAll() {
    /*                                                        */
    const deletedValueIds = gemerkt
      .map((title) => {
        const value = values.find((v) => v.title === title);
        return value?.id ?? "";
      })
      .filter(Boolean);

    gemerkt = [];
    resetAllSizes(sizes.map);
    dynamicSelectedValues = [];
    SizePreferencesCookieStore.deleteAll();
    showDeletionBanner = true;

    /*                                      */
    heureka.filterPersonalization_track.emit({
      action: "delete_all",
      facetId: id,
      values: deletedValueIds,
    });
  }

  function handleRestoreCookie() {
    SizePreferencesCookieStore.restoreCookie();
    const restoredSizes = SizePreferencesCookieStore.get() ?? { ...DEFAULT_SIZE_PREFERENCES };

    restoreSizes(sizes.map, restoredSizes);
    gemerkt = extractSize(restoredSizes, id);
    dynamicSelectedValues = [];
    showDeletionBanner = false;

    /*                                   */
    const savedValuesIds = gemerkt
      .map((title) => {
        const value = values.find((v) => v.title === title);
        return value?.id ?? "";
      })
      .filter(Boolean);

    /*                                      */
    heureka.filterPersonalization_track.emit({
      action: "undo_delete",
      facetId: id,
      values: savedValuesIds,
    });
  }

  function handleSelection({ target }: MouseEvent) {
    if (!(target instanceof HTMLInputElement)) return;

    const targetId = target.id;
    const isAlreadySelected = dynamicSelectedValues.some((v) => v.id === targetId);

    if (target.checked && !isAlreadySelected) {
      const newValue = values.find((v) => v.id === targetId);
      if (newValue) {
        dynamicSelectedValues = [...dynamicSelectedValues, newValue];
      }
      preselected.set({ ...$preselected, [targetId]: true });
    } else if (!target.checked) {
      dynamicSelectedValues = dynamicSelectedValues.filter((v) => v.id !== targetId);
      preselected.set({ ...$preselected, [targetId]: false });
    }
  }

  function closeDeletionBanner() {
    showDeletionBanner = false;
  }

  /*                                                                           */
  /*                                                               */
  /*                                                                           */

  function getOcDetailSheet(): HTMLElement | null {
    return document.querySelector("#heureka-filterdetailsheet");
  }

  function getSheetContent(): HTMLElement | null {
    return isActive("HEUREKA_1913_MIGRATE_TO_OC_SHEET")
      ? (getOcDetailSheet()?.querySelector(".heureka_filterDetailSheet__content") ?? null)
      : document.querySelector(".pl_sheet__content");
  }

  let isSticky = $state(false);
  const sheetContent: HTMLElement | null = getSheetContent();
  const shadowContent: HTMLElement | undefined | null =
    getOcDetailSheet()?.shadowRoot?.querySelector(".sheet__content");

  $effect(() => {
    if (mode !== "sheetSecondLevel" || !sheetContent) return;

    const checkOverflow = () => {
      if (sheetContent) {
        if (isActive("HEUREKA_1913_MIGRATE_TO_OC_SHEET") && shadowContent) {
          /*                                                              */
          isSticky = shadowContent.scrollHeight > shadowContent.clientHeight;
        } else {
          isSticky = sheetContent.scrollHeight > sheetContent.clientHeight;
        }
      }
    };

    /*            */
    checkOverflow();

    const mutationObserver = new MutationObserver(checkOverflow);

    mutationObserver.observe(sheetContent, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  });
</script>

<!-- ============================================================================
     Template
     ============================================================================ -->

{#snippet defaultList(facetValues: FacetValue[])}
  <List values={facetValues}></List>
{/snippet}

<div>
  <fieldset
    id={facetId}
    {name}
    class={["hc_facet", rest.class]}
    data-facet-visibility={visibility}
    data-has-nba-values={hasNbaValues}
    {...featureTrackingAttributes(removeStatusIf(hidden, featureTracking))}
    {hidden}
  >
    {#if showTitle}
      <legend>
        <span>{title}</span>
      </legend>
    {/if}

    <div onclick={handleSelection} role="none">
      {#if mode === "sheetFirstLevel" && filterTitleMode === "onlyPopularValuesAndTitle" && !hidden}
        {@render contentSnippet(selectedValues.filter(isNotPopular), "selected")}
        {@render contentSnippet(unselectedNbas, "unselected")}
        {#if popularValues.length > 0}
          <div class="hc_facet__scrollBox">
            {@render contentSnippet(popularValues, "unselectedPopular")}
          </div>
        {/if}
      {:else}
        {#key savedNamedValues}
          {@render contentSnippet(savedNamedValues, "unselected")}
        {/key}
      {/if}
    </div>
  </fieldset>

  <div id="heureka_personalization" class={["hc_personalization", { "hc_personalization--sticky": isSticky }]}>
    {#if showDeletionBanner}
      <oc-banner-v1
        class="hc_personalization__deletion-banner"
        size="100"
        variant="success"
        icon-type="success-hint"
        onoc-banner-close={closeDeletionBanner}
      >
        Alle gemerkten Größen wurden entfernt.<br />
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <oc-link-v2 variant="over-color" size="75" class="hc_personalization__link" onclick={handleRestoreCookie}>
          Rückgängig
        </oc-link-v2>
      </oc-banner-v1>
    {/if}

    <oc-card-v2 elevation-level="stacked" background-color="#f5e6d7">
      <div class="hc_personalization__content">
        <div class="hc_personalization__column">
          <span class="hc_personalization__headline oc-headline-25">
            {#if !merken && hasAnySavedSizes(sizes.map)}
              Gemerkte Größe{shouldBePlural ? "n" : ""}
            {:else}
              Schuhgröße{shouldBePlural ? "n" : ""} auf deinem Gerät für 30 Tage merken
            {/if}
          </span>
          <br />
          <span class="hc_personalization__text oc-copy-75">
            {#if dynamicSelectedValues.length > 0 && selectedValues.map((v) => v.title) !== gemerkt}
              {#each dynamicSelectedValues as sv, index (index)}
                {#if index > 0};{/if}
                {getSizeLabel(facetId)}
                {sv.title}
              {/each}
            {:else if hasAnySavedSizes(sizes.map)}
              {formatSizePreferences(sizes.map)}
            {:else}
              Wähle deine passende Größe
            {/if}
          </span>
          <br />
          <oc-toggletip-v1 position="top" style="--background-color: none;">
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <oc-link-v2
              variant="over-color"
              size="75"
              class="hc_personalization__link"
              onclick={() => {
                tracking.submitEvent({
                  san_FacetActivity: ["mehr_erfahren_open"],
                  san_FacetActivityType: [name],
                });
              }}
            >
              Mehr erfahren
            </oc-link-v2>
            <span slot="toggletip-content">
              Spare Zeit beim Filtern. Du kannst deine Größen auf deinem Gerät für 30 Tage speichern.
            </span>
          </oc-toggletip-v1>
        </div>

        {#if showActionButton}
          <div class="hc_personalization__column">
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            {#if entfernen}
              <oc-button-v1
                icon-type-left="wishlist-active"
                variant="secondary-over-color"
                size="50"
                onclick={handleDeleteCookieValues}
              >
                Entfernen
              </oc-button-v1>
            {:else if merken}
              <oc-button-v1
                icon-type-left="wishlist"
                variant="secondary-over-color"
                size="50"
                onclick={handleSaveCookie}
              >
                Merken
              </oc-button-v1>
            {:else if alleEntfernen}
              <oc-button-v1
                icon-type-left="wishlist-active"
                variant="secondary-over-color"
                size="50"
                onclick={handleDeleteAll}
              >
                Entfernen
              </oc-button-v1>
            {/if}
          </div>
        {/if}
      </div>
    </oc-card-v2>
  </div>
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  fieldset {
    min-width: 0;

    span {
      font: tokens.$oc-semantic-font-copy-100;
      font-weight: 700;
    }
  }

  .hc_personalization {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: tokens.$oc-semantic-spacing-100;
    border-top: tokens.$oc-semantic-border-width-100 solid tokens.$oc-semantic-color-frame-background;
    margin: tokens.$oc-semantic-spacing-100 calc(-1 * var(--oc-semantic-spacing-100)) 0;
    background-color: tokens.$oc-semantic-color-canvas-background;

    span {
      font: unset;
      font-weight: unset;
    }

    &--sticky {
      position: sticky;
      bottom: calc(-1 * var(--oc-semantic-spacing-100));
      width: auto;
    }

    &__content {
      padding: tokens.$oc-semantic-spacing-75;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 100%;
    }

    &__deletion-banner {
      margin-bottom: tokens.$oc-semantic-spacing-75;
    }

    &__divider {
      margin-top: 12px;
      margin-bottom: 12px;
    }
  }
</style>
